import React, { useState, useEffect, useRef } from 'react';
import { initMercadoPago, Payment } from '@mercadopago/sdk-react';
import apiPublico from '../../services/apiPublico';
import s from '../../pages/Loja.module.css';

const fmtBRL = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Traduz o "selectedPaymentMethod" do Brick pro rótulo que aparece no PDV
// (F2 Pré-venda) e na regra de fidelidade (que já reconhece essas strings —
// ver isMercadoPago/ehAVista em vendasController.js).
const rotuloTipo = { bank_transfer: 'pix', credit_card: 'credito', debit_card: 'debito' };

/**
 * Checkout embutido na vitrine (Mercado Pago Payment Brick) — dados de
 * contato → escolhe PIX/débito/crédito até 3x → confirma → aguarda (PIX
 * pode demorar o cliente pagar) → sucesso com nº do pedido, já aprovado e
 * disponível pra Pré-venda (F2) do PDV.
 *
 * Props: slug, itensSacola (entries de Object.entries da sacola), total,
 * onFechar, onPedidoConfirmado(pedidoId).
 */
export default function CheckoutMercadoPago({ slug, itensSacola, total, onFechar, onPedidoConfirmado }) {
  const [etapa, setEtapa] = useState('dados'); // dados | pagamento | aguardando | sucesso
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const temCurso = itensSacola.some(([, it]) => it.eh_curso);
  const [publicKey, setPublicKey] = useState(null);
  const [pagamentoId, setPagamentoId] = useState(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [pix, setPix] = useState(null);
  const [pedidoId, setPedidoId] = useState(null);
  const inicializadoRef = useRef(false);

  useEffect(() => {
    apiPublico.get(`/catalogo/${slug}/checkout/config`)
      .then(({ data }) => {
        if (!data.disponivel) { setErro('Pagamento online não está disponível nesta loja no momento.'); return; }
        setPublicKey(data.public_key);
        if (!inicializadoRef.current) {
          initMercadoPago(data.public_key, { locale: 'pt-BR' });
          inicializadoRef.current = true;
        }
      })
      .catch(() => setErro('Erro ao carregar o pagamento. Tente novamente.'));
  }, [slug]);

  const iniciarPagamento = async (e) => {
    e.preventDefault();
    setErro(''); setEnviando(true);
    try {
      const { data } = await apiPublico.post(`/catalogo/${slug}/checkout/iniciar`, {
        itens: itensSacola.map(([produtoId, it]) => ({ produto_id: Number(produtoId), quantidade: it.quantidade })),
        cliente_nome: nome.trim() || null,
        cliente_telefone: telefone.trim() || null,
        cliente_email: temCurso ? email.trim() : (email.trim() || null),
        cliente_senha: temCurso ? senha : undefined,
      });
      setPagamentoId(data.pagamento_id);
      setEtapa('pagamento');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao iniciar o pagamento.');
    } finally {
      setEnviando(false);
    }
  };

  const enviarPagamento = async ({ selectedPaymentMethod, formData }) => {
    setErro('');
    const tipoPagamento = rotuloTipo[selectedPaymentMethod] || selectedPaymentMethod;
    try {
      const { data } = await apiPublico.post(`/catalogo/${slug}/checkout/${pagamentoId}/pagar`, {
        tipo_pagamento: tipoPagamento,
        ...formData,
      });
      if (data.status === 'aprovado') {
        setPedidoId(data.pedido_id);
        setEtapa('sucesso');
        onPedidoConfirmado?.(data.pedido_id);
      } else if (data.status === 'recusado') {
        setErro('Pagamento recusado. Confira os dados ou tente outra forma de pagamento.');
      } else {
        setPix(data.pix); // PIX pendente — mostra QR Code e aguarda confirmação
        setEtapa('aguardando');
      }
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao processar o pagamento. Tente novamente.');
    }
  };

  // Polling enquanto aguarda confirmação (PIX demora o cliente pagar de verdade)
  useEffect(() => {
    if (etapa !== 'aguardando' || !pagamentoId) return;
    const t = setInterval(async () => {
      try {
        const { data } = await apiPublico.get(`/catalogo/${slug}/checkout/${pagamentoId}/status`);
        if (data.status === 'aprovado') {
          setPedidoId(data.pedido_id);
          setEtapa('sucesso');
          onPedidoConfirmado?.(data.pedido_id);
        } else if (data.status === 'recusado') {
          setErro('O pagamento não foi aprovado.');
          setEtapa('pagamento');
        }
      } catch { /* tenta de novo no próximo ciclo */ }
    }, 3000);
    return () => clearInterval(t);
  }, [etapa, pagamentoId, slug]);

  return (
    <div className={s.modalFundo} onClick={etapa === 'sucesso' ? undefined : onFechar}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        {etapa !== 'sucesso' && <button className={s.modalFechar} onClick={onFechar}>✕</button>}

        {etapa === 'dados' && (
          <form onSubmit={iniciarPagamento}>
            <h2 className={s.modalNome}>Seus dados</h2>
            <p className={s.ajudaSacola} style={{ marginBottom: 12 }}>
              Só pra combinar a entrega/retirada — o pagamento é confirmado direto com o Mercado Pago.
            </p>
            <div className={s.campoCheckout}>
              <label>Nome</label>
              <input className={s.inputCheckout} value={nome} onChange={e => setNome(e.target.value)} required />
            </div>
            <div className={s.campoCheckout}>
              <label>WhatsApp</label>
              <input className={s.inputCheckout} value={telefone} onChange={e => setTelefone(e.target.value)}
                placeholder="(00) 00000-0000" required />
            </div>
            {temCurso && (
              <>
                <p className={s.ajudaSacola} style={{ marginBottom: 8 }}>
                  Sua sacola tem um curso em vídeo — crie uma senha agora, você vai usar
                  esse e-mail e senha pra entrar e assistir depois que o pagamento for confirmado.
                </p>
                <div className={s.campoCheckout}>
                  <label>E-mail</label>
                  <input type="email" className={s.inputCheckout} value={email}
                    onChange={e => setEmail(e.target.value)} required />
                </div>
                <div className={s.campoCheckout}>
                  <label>Crie uma senha (mín. 6 caracteres)</label>
                  <input type="password" className={s.inputCheckout} value={senha}
                    onChange={e => setSenha(e.target.value)} minLength={6} required />
                </div>
              </>
            )}
            {erro && <div className={s.erroCheckout}>{erro}</div>}
            <button type="submit" className={s.btnPagar} disabled={enviando || !publicKey}>
              {enviando ? 'Preparando...' : `Continuar — ${fmtBRL(total)}`}
            </button>
          </form>
        )}

        {etapa === 'pagamento' && publicKey && (
          <>
            <h2 className={s.modalNome}>Pagamento — {fmtBRL(total)}</h2>
            {erro && <div className={s.erroCheckout}>{erro}</div>}
            <Payment
              initialization={{ amount: total }}
              customization={{
                paymentMethods: {
                  creditCard: 'all',
                  debitCard: 'all',
                  bankTransfer: 'all', // PIX
                  maxInstallments: 3,
                },
              }}
              onSubmit={enviarPagamento}
              onError={() => setErro('Erro ao carregar as opções de pagamento.')}
            />
          </>
        )}

        {etapa === 'aguardando' && (
          <div style={{ textAlign: 'center' }}>
            <h2 className={s.modalNome}>Aguardando confirmação do PIX</h2>
            {pix?.qr_code_base64 && (
              <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code PIX"
                style={{ width: 220, height: 220, margin: '12px auto' }} />
            )}
            {pix?.qr_code && (
              <div className={s.campoCheckout}>
                <label>Copia e cola</label>
                <textarea className={s.inputCheckout} readOnly value={pix.qr_code}
                  style={{ minHeight: 60, fontSize: '0.7rem' }} onClick={e => e.target.select()} />
              </div>
            )}
            <p className={s.ajudaSacola}>
              Assim que o pagamento cair, essa tela atualiza sozinha — pode deixar aberta.
            </p>
          </div>
        )}

        {etapa === 'sucesso' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem' }}>✅</div>
            <h2 className={s.modalNome}>Pagamento confirmado!</h2>
            <p className={s.ajudaSacola} style={{ fontSize: '0.9rem' }}>
              Pedido <strong>#{pedidoId}</strong> gerado — a loja já pode atender.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
